/**
 * Input — chat input row.
 *
 *   - Enter: send.
 *   - Tab: complete slash command OR @username from the active room.
 *   - When the current "word" starts with '/': suggest commands.
 *   - When the current "word" starts with '@': suggest usernames.
 */
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { SOUNDS } from '../lib/slashCommands.js';
import type { UserListEntry } from '../lib/types.js';

interface Props {
    disabled?: boolean;
    placeholder?: string;
    /** Active-room users — drives @-completion. */
    users?: UserListEntry[];
    onSend: (text: string) => void;
}

const STATIC_CMDS = [
    'pm', 'msg', 'reply', 'ignore', 'unignore', 'me', 'profile', 'room',
    'clear', 'cls', 'help', 'soundboards', 'login',
    'bomb', 'walrus', 'bucket',
    'play_connect4', 'c4', 'connect4', 'c4stats',
    'rotate', 'invert', 'bbc', 'body', 'font',
    'siu', 'txt',
];

interface Suggestion { kind: 'cmd' | 'user'; text: string; color?: string; }

/** Find the active "word" the cursor is in and what to complete. */
function activeWord(input: string, cursor: number): { start: number; end: number; word: string } {
    // walk back/forward to whitespace
    let s = cursor;
    while (s > 0 && !/\s/.test(input[s - 1]!)) s--;
    let e = cursor;
    while (e < input.length && !/\s/.test(input[e]!)) e++;
    return { start: s, end: e, word: input.slice(s, e) };
}

export function Input({ disabled, placeholder, users = [], onSend }: Props) {
    const [text, setText] = useState('');
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const suggestions = useMemo<Suggestion[]>(() => {
        const { word } = activeWord(text, cursor);
        if (word.startsWith('/') && word.length >= 1) {
            const head = word.slice(1).toLowerCase();
            const all = [...STATIC_CMDS, ...Object.keys(SOUNDS)];
            return Array.from(new Set(all.filter(c => c.startsWith(head))))
                .slice(0, 8)
                .map(c => ({ kind: 'cmd', text: '/' + c }));
        }
        if (word.startsWith('@') && word.length >= 1) {
            const head = word.slice(1).toLowerCase();
            return users
                .filter(u => u.name.toLowerCase().startsWith(head))
                .slice(0, 8)
                .map(u => ({ kind: 'user', text: '@' + u.name, color: u.color }));
        }
        return [];
    }, [text, cursor, users]);

    function commit() {
        const t = text.trim();
        if (!t) return;
        onSend(t);
        setText('');
        setCursor(0);
    }

    function applyCompletion(s: Suggestion) {
        const { start, end } = activeWord(text, cursor);
        const before = text.slice(0, start);
        const after  = text.slice(end);
        const replacement = s.text + (after.startsWith(' ') ? '' : ' ');
        const next = before + replacement + after;
        const newCursor = (before + replacement).length;
        setText(next);
        setCursor(newCursor);
        // Move the actual DOM cursor too (next tick)
        setTimeout(() => {
            const el = inputRef.current; if (!el) return;
            el.focus();
            el.setSelectionRange(newCursor, newCursor);
        }, 0);
    }

    function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            commit();
        } else if (e.key === 'Tab' && suggestions.length > 0) {
            e.preventDefault();
            applyCompletion(suggestions[0]!);
        }
    }

    return (
        <div className="input-row">
            <input
                ref={inputRef}
                className="input"
                disabled={disabled}
                placeholder={placeholder ?? 'Type a message, or /help for commands'}
                value={text}
                onChange={e => { setText(e.target.value); setCursor(e.target.selectionStart ?? e.target.value.length); }}
                onKeyUp={e => setCursor((e.target as HTMLInputElement).selectionStart ?? text.length)}
                onClick={e => setCursor((e.target as HTMLInputElement).selectionStart ?? text.length)}
                onKeyDown={onKeyDown}
                autoFocus
            />
            <button className="send-btn" disabled={disabled || !text.trim()} onClick={commit}>Send</button>
            {suggestions.length > 0 && (
                <ul className="autocomplete">
                    {suggestions.map((s, i) => (
                        <li key={i}
                            className="ac-item"
                            onClick={() => applyCompletion(s)}>
                            {s.kind === 'user'
                                ? <span style={{ color: s.color }}>{s.text}</span>
                                : s.text}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
